import { MMKV } from 'react-native-mmkv';

import {
  CookieName,
  LocalStorageKey,
  SessionStorageKey,
} from '@scatterlab/zeta-service/constants/common';

import { Country, Gender, TokenIssuer } from './apis/types';

import type { PlotOrderByOption } from '@scatterlab/zeta-service/hooks/usePlotOrderBy';

export interface StorageValue {
  [CookieName.Token]: string;
  [CookieName.RefreshToken]: string;
  [CookieName.DeviceId]: string;
  [CookieName.Country]: Country;

  [LocalStorageKey.HomeTabClicked]: boolean;

  [LocalStorageKey.ExampleConversationGuideHidden]: boolean;
  [LocalStorageKey.NSFWNoticeModalShown]: boolean;
  [LocalStorageKey.UnresolvedLinkingPath]: string;
  [LocalStorageKey.AnonymousAcceptCookie]: boolean;
  [LocalStorageKey.NotificationPopupShown]: boolean;
  [LocalStorageKey.Country]: Country;
  [LocalStorageKey.Locale]: string;
  [LocalStorageKey.AppDownloadModalShown]: string;

  [LocalStorageKey.LastProDuration]: number;
  [LocalStorageKey.LastPassType]: string;
  [LocalStorageKey.PassNudgeShown]: boolean;
  [LocalStorageKey.PassNudgeOneShownDate]: string;
  [LocalStorageKey.PassNudgeTwoShownDate]: string;
  [LocalStorageKey.RePurchaseNudgeCount]: number;

  [LocalStorageKey.ProModalContext]: string;
  [LocalStorageKey.PurchaseRedirectPath]: string;
  [LocalStorageKey.PurchasingProductId]: string;

  [LocalStorageKey.NotiLongPressChangedBGShown]: boolean;
  [LocalStorageKey.ChatBGChangeFeedback]: string;
  [LocalStorageKey.CharacterRankingGender]: string;
  [LocalStorageKey.BackgroundImagePassNudgeShown]: boolean;
  [LocalStorageKey.BackgroundImageGuideShown]: boolean;
  [LocalStorageKey.FacialImageGuideShown]: boolean;
  [LocalStorageKey.AnonymousChatCountByRoom]: string;
  [LocalStorageKey.ShownAnnouncementPopupIdList]: string;
  [LocalStorageKey.CharacterListOrderByInMyCharacterList]: PlotOrderByOption;
  [LocalStorageKey.CharacterListOrderByInCreatorProfile]: PlotOrderByOption;

  [LocalStorageKey.InterstitialAdRequiredTime]: number;
  [LocalStorageKey.LastInterstitialVersion]: string;
  [LocalStorageKey.RewardAdTriggeredTime]: number;
  [LocalStorageKey.OriginalProModalShown]: boolean;
  [LocalStorageKey.AdNudgeShownContentList]: string;

  [LocalStorageKey.ATTModalContext]: string;
  [LocalStorageKey.LastSeenInviteeCount]: number;
  [LocalStorageKey.LastSlowChatNudgeTime]: number;
  [LocalStorageKey.LastInAppReviewRequestDate]: string;
  [LocalStorageKey.QuickReplyGuideShown]: boolean;
  [LocalStorageKey.QuickReplySwipeGuideShown]: boolean;
  [LocalStorageKey.QuickReplyPurchasePopupHidden]: boolean;

  [LocalStorageKey.StaticSkinNoticeShown]: boolean;

  [LocalStorageKey.TTSConfirmGuideShown]: boolean;
  [LocalStorageKey.TTSAutoGeneratePopupShownRecord]: string;
  [LocalStorageKey.TTSFeedback]: string;

  [LocalStorageKey.IntroSkipSetting]: boolean;

  [LocalStorageKey.SafetyGuideBottomSheetOnCreatingCharacterShown]: boolean;

  [LocalStorageKey.DiscoveryGuideShown]: boolean;
  [LocalStorageKey.DiscoveryGender]: Gender;

  [LocalStorageKey.LastConsecutiveTurnsAllowPushNudgeDate]: Date;

  [LocalStorageKey.SeenStoryEpisodes]: string;
  [LocalStorageKey.SeenStories]: string;
  [LocalStorageKey.FavoriteStoryNudgeClosed]: boolean;
  [LocalStorageKey.StoryTooltipShown]: boolean;

  [LocalStorageKey.CharacterProfileGuideShown]: boolean;
  [LocalStorageKey.CharacterProfileSaveGuideShown]: boolean;
  [LocalStorageKey.AddTagOneTimeGuideShown]: boolean;

  [LocalStorageKey.DebugMode]: boolean;

  [LocalStorageKey.RecentSearchKeywords]: string;

  [LocalStorageKey.DoNotSuicide]: {
    dates: Date[];
    alertDate?: Date;
  };

  [LocalStorageKey.RedirectUrl]: string;

  [LocalStorageKey.LastLoginMethod]: TokenIssuer;

  [SessionStorageKey.RedirectPath]: string;
  [SessionStorageKey.WebToken]: string;
}

export const mmkvInstance = new MMKV();

const isPrimitive = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

const replacer = (key: string, value: unknown) => {
  if (value instanceof Date) {
    return `Date(${+value})`;
  }
  return value;
};

const reviver = (key: string, value: unknown) => {
  if (typeof value === 'string') {
    if (value.startsWith('Date(') && value.endsWith(')')) {
      return new Date(Number(value.slice(5, -1)));
    }
  }
  return value;
};

export default {
  getString: <T extends keyof StorageValue>(
    key: StorageValue[T] extends string ? string : T,
  ): StorageValue[T] | undefined =>
    mmkvInstance.getString(key) as StorageValue[T] | undefined,
  getNumber: <T extends keyof StorageValue>(
    key: StorageValue[T] extends number ? string : T,
  ) => mmkvInstance.getNumber(key),
  getBoolean: <T extends keyof StorageValue>(
    key: StorageValue[T] extends boolean ? string : T,
  ) => mmkvInstance.getBoolean(key),
  getBuffer: <T extends keyof StorageValue>(
    key: StorageValue[T] extends Uint8Array ? string : T,
  ) => mmkvInstance.getBuffer(key),
  getDate: <T extends keyof StorageValue>(
    key: StorageValue[T] extends Date ? string : T,
  ) => {
    const dateString = mmkvInstance.getString(key);
    return dateString ? new Date(dateString) : undefined;
  },
  getObject: <T extends keyof StorageValue>(
    key: StorageValue[T] extends object ? string : T,
  ): StorageValue[T] | undefined => {
    const jsonString = mmkvInstance.getString(key);
    return jsonString ? JSON.parse(jsonString, reviver) : undefined;
  },
  set: <T extends keyof StorageValue>(key: T, value: StorageValue[T]) => {
    if (isPrimitive(value)) {
      mmkvInstance.set(key, value);
    } else if (value instanceof Date) {
      mmkvInstance.set(key, value.toISOString());
    } else if (
      typeof value === 'object' &&
      (value as object).constructor?.name === 'Uint8Array'
    ) {
      mmkvInstance.set(key, value as unknown as Uint8Array);
    } else {
      mmkvInstance.set(key, JSON.stringify(value, replacer));
    }
  },
  contains: (key: keyof StorageValue) => mmkvInstance.contains(key),
  delete: (key: keyof StorageValue) => mmkvInstance.delete(key),
  getAllKeys: () => mmkvInstance.getAllKeys(),
  clearAll: () => mmkvInstance.clearAll(),
  recrypt: (key?: keyof StorageValue) => mmkvInstance.recrypt(key),
  toString: () => mmkvInstance.toString(),
  toJSON: () => mmkvInstance.toJSON(),
  addOnValueChangedListener: (
    onValueChanged: (key: keyof StorageValue) => void,
  ) =>
    mmkvInstance.addOnValueChangedListener(
      onValueChanged as (key: string) => void,
    ),
};
